import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NoteResult, formatDate } from '@/lib/search-utils';
import { Button } from '@/components/ui/button';
import { Link2, SquareArrowOutUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SearchDetailShell, SEARCH_DETAIL_CARD_CLASS } from '@/components/search/search-detail-shell';

function NoteSearchTabs({
  active,
  onChange,
}: {
  active: 'content' | 'related';
  onChange: (v: 'content' | 'related') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-t border-border/70 bg-background px-4 sm:px-6">
      <div className="flex gap-6">
        <button
          type="button"
          onClick={() => onChange('content')}
          className={cn(
            'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
            active === 'content'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {t('search.noteTabContent')}
        </button>
        <button
          type="button"
          onClick={() => onChange('related')}
          className={cn(
            'border-b-2 px-1 py-3 text-sm font-medium transition-colors',
            active === 'related'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          {t('search.noteTabRelated')}
        </button>
      </div>
    </div>
  );
}

export default function NoteSearchDetail({
  note,
  onNavigate,
}: {
  note: NoteResult;
  onNavigate: (dealId: string) => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'content' | 'related'>('content');

  const preview =
    (note.body?.trim().slice(0, 120) ?? '') + (note.body && note.body.length > 120 ? '…' : '');
  const hasPreview = Boolean(note.body?.trim());

  const subtitleNode =
    note.dealTitle != null && note.dealTitle !== '' ? (
      <span className="line-clamp-2 block">{note.dealTitle}</span>
    ) : hasPreview ? (
      <span className="line-clamp-2 block">{preview}</span>
    ) : undefined;

  const titleEl = (
    <h2 className="break-words text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
      {t('search.noteSearchDetailTitle')}
    </h2>
  );

  const meta =
    note.createdAt != null && note.createdAt !== '' ? (
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <span className="font-medium">{t('search.noteDateLabel')}</span> {formatDate(note.createdAt)}
        </div>
      </div>
    ) : undefined;

  return (
    <SearchDetailShell
      title={titleEl}
      subtitle={subtitleNode}
      meta={meta}
      tabs={<NoteSearchTabs active={tab} onChange={setTab} />}
      onOpenFullPage={() => {
        if (note.dealId) onNavigate(note.dealId);
      }}
      openFullPageTitle={note.dealId ? t('search.openFullPageTitle') : t('search.openFullPageNoDeal')}
      openFullPageDisabled={!note.dealId}
    >
      {tab === 'content' ? (
        <div className={cn(SEARCH_DETAIL_CARD_CLASS, 'space-y-0')}>
          <p className="whitespace-pre-wrap text-sm text-foreground">{note.body || '—'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className={cn(SEARCH_DETAIL_CARD_CLASS, 'space-y-4')}>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              {t('search.noteRelatedHeading')}
            </h3>
            <div className="space-y-3 text-sm">
              {note.dealTitle ? (
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">{t('search.noteRelatedDeal')}</span>
                  <p className="font-medium text-foreground">{note.dealTitle}</p>
                </div>
              ) : null}
              {note.contactName ? (
                <div>
                  <span className="mb-1 block text-xs text-muted-foreground">{t('deal.relatedContactHeading')}</span>
                  <p className="font-medium text-foreground">{note.contactName}</p>
                  {note.contactEmail ? (
                    <p className="mt-1 text-xs text-muted-foreground">{note.contactEmail}</p>
                  ) : null}
                </div>
              ) : null}
              {!note.dealTitle && !note.contactName ? (
                <p className="text-sm text-muted-foreground">{t('search.noteNoRelated')}</p>
              ) : null}
            </div>
          </div>
          {note.dealId ? (
            <Button
              type="button"
              variant="outline"
              className="h-9 w-full gap-2 font-medium sm:w-auto"
              onClick={() => onNavigate(note.dealId!)}
            >
              <SquareArrowOutUpRight className="h-4 w-4 shrink-0" aria-hidden />
              {t('search.openRelatedDeal')}
            </Button>
          ) : null}
        </div>
      )}
    </SearchDetailShell>
  );
}
